import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from '../motion.ts';

/**
 * The hero's picture of the whole idea, in one loop: a buyer asks, the agent
 * thinks, and the answer is *on the product*. It is the film's opening and its
 * thumbnail — the pill, the orb, the reticle that moves from the question to the
 * answer — drawn in the page rather than played.
 *
 * It is illustrative, and says so: the questions are the three the section
 * below lets you press, and the figures are that section's example data. It is
 * decoration to a screen reader (the hero's own text says all of it), and it
 * stops when it is off screen, when the tab is hidden, and under reduced motion
 * (which holds the finished frame).
 */
const LOOPS = [
  { q: 'Where do people drop off?', say: 'Let’s look at the signup journey.', view: 'Funnels', rows: [['Visited', 100], ['Signed up', 65], ['Workspace', 42], ['Invited', 25]] },
  { q: 'Can I compare different audiences?', say: 'Here’s the audience comparison.', view: 'Audiences', rows: [['Product', 68], ['Sales', 54], ['Ops', 43], ['Other', 35]] },
  { q: 'What happens after signup?', say: 'Let me show you the onboarding journey.', view: 'Activation', rows: [['Workspace', 100], ['Data source', 82], ['First report', 64], ['Shared', 46]] },
] as const;

// 0 asking · 1 thinking · 2 showing · 3 letting go
const HOLD = [0, 1000, 3600, 700];
const PER_CHAR = 46;

export function HeroStage() {
  const root = useRef<HTMLDivElement>(null);
  const live = useInView(root, 0.25);
  const reduced = useReducedMotion();
  const [loop, setLoop] = useState(0);
  const [phase, setPhase] = useState(0);
  const [chars, setChars] = useState(0);
  const item = LOOPS[loop]!;
  const running = live && !reduced;

  // typing: phase 0 ends when the question does
  useEffect(() => {
    if (!running || phase !== 0) return;
    if (chars >= item.q.length) { const t = setTimeout(() => setPhase(1), 380); return () => clearTimeout(t); }
    const t = setTimeout(() => setChars(c => c + 1), chars === 0 ? 700 : PER_CHAR);
    return () => clearTimeout(t);
  }, [running, phase, chars, item.q.length]);

  // the rest of the loop
  useEffect(() => {
    if (!running || phase === 0) return;
    const t = setTimeout(() => {
      if (phase === 3) { setLoop(n => (n + 1) % LOOPS.length); setChars(0); setPhase(0); } else setPhase(p => p + 1);
    }, HOLD[phase]);
    return () => clearTimeout(t);
  }, [running, phase]);

  const shown = reduced ? item.q : item.q.slice(0, chars);
  const p = reduced ? 2 : phase;
  return (
    <div className="hero-stage" ref={root} data-phase={p} aria-hidden="true">
      <div className="hs-field"><i/><i/><i/></div>
      <div className={'hs-reticle' + (p >= 2 ? ' on-answer' : '')}><i/><i/><i/><i/></div>
      <div className="hs-pill">
        <span className="hs-avatar">M</span>
        <span className="hs-q">{shown}{p === 0 && <i className="hs-caret"/>}</span>
        <span className={'hs-send' + (p >= 1 ? ' sent' : '')}><svg viewBox="0 0 16 16"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5"/></svg></span>
      </div>
      <div className={'hs-wire' + (p >= 1 ? ' drawn' : '')}/>
      <img className={'hs-orb' + (p === 1 ? ' thinking' : '')} src="/agent-thinking.svg" alt="" width="220" height="220"/>
      <div className={'hs-answer' + (p === 2 ? ' shown' : '')} key={loop}>
        <div className="hs-answer-head"><span>PERSIVO</span><span>{item.view} <b>·</b> example data</span></div>
        <p>{item.say}</p>
        <div className="hs-rows">
          {item.rows.slice(0, 3).map(([label, v], i) => (
            <div key={label} style={{ ['--i' as string]: i }}><span>{label}</span><i><b style={{ ['--w' as string]: v + '%' }}/></i><em>{v}%</em></div>
          ))}
        </div>
      </div>
    </div>
  );
}
